<template>
  <div class="layout-regions">
    <MobileHeader
      :class="{ 'hide': showRegions }"
      :label="$t('nav.issues')"
    >
      <div class="menu">
        <ul class="menu__items">
          <li class="menu-item">
            <button
              :aria-label="$t('regions.search_regions_and_issues')"
              class="menu-item__link menu-item__link--icon"
              type="button"
              @click="openMobileSearch"
            >
              <div class="menu-item__label">
                <UseSvg
                  class="icon"
                  name="search"
                />
              </div>
            </button>
          </li>
        </ul>
      </div>
    </MobileHeader>

    <div :class="{ 'layout-regions__header hide-for-medium': true, 'hide': showRegions }">
      <button
        class="select__trigger typo-ignore typo-title"
        type="button"
        @click="showRegions = true"
      >
        <div class="select__label-wrapper">
          <PolygonSvg
            v-if="selectedRegion"
            :bounds="selectedRegion.bounds"
            :class="`region-item__thumbnail region-item__thumbnail--no-margin region-item__thumbnail--${selectedRegion.region_type}`"
            :project="false"
            :size="20"
          />
          <span class="select__label">
            {{ selectedRegion ? selectedRegion.name : $t('regions.all_regions') }}
          </span>
        </div>
        <div class="select__arrow">
          <UseSvg
            class="icon icon--small"
            name="arrow_down_16"
          />
        </div>
      </button>
      <SwitchButton
        v-model="currentView"
        :items="viewOptions"
        @update:modelValue="refreshView"
      />
    </div>

    <div :class="{ 'layout-regions__regions': true, 'show-for-medium': !showRegions }">
      <div class="layout-regions__regions-header hide-for-medium">
        <button
          type="button"
          @click="showRegions = false"
        >
          <UseSvg
            class="icon"
            name="close"
          />
        </button>
        <span class="layout-regions__regions-title">{{ $t('regions.select_region') }}</span>
      </div>

      <div class="layout-regions__search-bar show-for-medium">
        <UseSvg
          :class="{ 'icon': true, 'icon--darker': !regionSearch }"
          name="search"
        />
        <input
          v-model="regionSearch"
          class="layout-regions__search-input typo-small-title"
          :placeholder="$t('regions.search_all_regions')"
        >
        <button
          v-if="regionSearch"
          class="button button--tiny button--transparent"
          type="button"
          @click="regionSearch = ''"
        >
          <UseSvg
            class="icon icon--small"
            name="close_circle_16"
          />
        </button>
      </div>

      <RegionTree
        v-if="regionTree"
        :class="{ 'hide-for-medium': regionSearch }"
        :modifiers="['manage-scroll']"
        :project="false"
        :regionTree="regionTree"
        :selectedRegion="selectedRegion"
        @select="selectRegion"
      />

      <Loader
        v-if="!regionTree"
        class="show-for-medium"
        :modifiers="['height-fix']"
      />

      <EmptyMessage
        v-if="regionSearch && regionResults.length === 0"
        :message="$t('general.no_results')"
        :modifiers="['grow']"
      >
        <UseSvg name="magnifying_glass_circle" />
      </EmptyMessage>
      <template v-if="regionSearch && regionResults.length > 0">
        <div class="layout-regions__results-header typo-small">
          {{ $t('general.results') }}
        </div>
        <div class="layout-regions__regions-tree scroll-container">
          <ul>
            <RegionItem
              v-for="region in regionResults"
              :key="region.id"
              class="layout-regions__regions-tree"
              :ignoreSelected="true"
              :project="false"
              :region="region"
              :showChildren="false"
              @select="selectRegionFromSearch"
            />
          </ul>
        </div>
      </template>
    </div>

    <div :class="{ 'layout-regions__content': true, 'show-for-medium': showRegions }">
      <div class="layout-regions__issues">
        <div class="layout-regions__search-bar show-for-medium">
          <UseSvg
            :class="{ 'icon': true, 'icon--darker': !issueSearch }"
            name="search"
          />
          <input
            v-model="issueSearch"
            class="layout-regions__search-input typo-small-title"
            :placeholder="$t('regions.search_all_issues')"
          >
          <button
            v-if="issueSearch"
            class="button button--tiny button--transparent"
            type="button"
            @click="issueSearch = ''"
          >
            <UseSvg
              class="icon icon--small"
              name="close_circle_16"
            />
          </button>
        </div>

        <NoticeMessage
          v-if="issuesStore.loadingNotification && issuesStore.loading"
          iconStyle="info"
          :message="$t('general.long_load_message')"
          :modifiers="['margin']"
        />
        <IssuesSkeletonLoader
          v-if="issuesStore.loading"
          class="show-for-medium"
        />
        <div
          v-if="!issuesStore.loading"
          :class="{ 'layout-regions__issues-content': true, 'hide': issueSearch }"
        >
          <div class="layout-regions__current-region show-for-medium">
            {{ selectedRegion ? selectedRegion.name : $t('regions.all_regions') }}
          </div>
          <div class="layout-regions__issues-header">
            <span
              v-if="issueList"
              class="layout-regions__issues-count typo-small-nav-item"
            >
              {{ issueList.length }} {{ $t('activerecord.models.plants/issue', { count: issueList.length }) }}
            </span>

            <div
              v-if="issueList?.length > 0"
              class="layout-regions__csv-download typo-small"
            >
              <a :href="csvIssueDownloadPath || 'javascript:void(0);'">
                <div class="layout-regions__csv-download__link">
                  <span>{{ $t('general.csv') }}</span>
                  <PushDown />
                </div>
              </a>
            </div>
            <IssueFilter
              v-if="isActiveFilter || issueList?.length > 0"
              :sortOptions="sortByOptions"
              :typeFilter="false"
              @setFilterOptions="setFilterOptions"
            />
          </div>

          <RecyclerView
            v-if="issueList"
            ref="issueScrollView"
            :class="{ 'layout-regions__list': true, 'show-for-medium': currentView !== 'list' }"
            :count="issueList.length"
            :gap="0.125"
            :height="7"
            hideScrollingX
            name="layout-regions__list-item-"
          >
            <template #default="{ index, style, key }">
              <IssueCard
                :key="key"
                :isLocatable="true"
                isSmallCard
                :issue="issueList[index]"
                overrideIssuePath="equipmentRegionsIssue"
                :responsive="false"
                :selected="issueList[index] === selectedIssue"
                :showDescription="false"
                :style="style"
                @locate="selectIssueOnList(issueList[index])"
              />
            </template>
          </RecyclerView>
        </div>
        <Loader
          v-if="!issueList"
          class="show-for-medium"
        />
        <div
          v-if="issueSearch"
          class="layout-regions__issues-content"
        >
          <EmptyMessage
            v-if="issueSearch && issueResults.length === 0"
            :message="$t('general.no_results')"
          >
            <UseSvg name="magnifying_glass_circle" />
          </EmptyMessage>
          <template v-if="issueSearch && issueResults.length > 0">
            <div class="layout-regions__results-header typo-small">
              {{ $t('general.results') }}
            </div>
            <div class="layout-regions__list scroll-container">
              <ul>
                <IssueCard
                  v-for="issue in issueResults"
                  :key="issue.id"
                  isSmallCard
                  :issue="issue"
                  noLink
                  :responsive="false"
                  :showDescription="false"
                  @select="selectIssueFromSearch(issue)"
                />
              </ul>
            </div>
          </template>
        </div>
      </div>

      <div :class="{ 'layout-regions__map': true, 'show-for-medium': currentView !== 'main', 'layout-regions__map--center': currentEquipment?.has_pending_tile_process }">
        <!-- Mobile loader for issues header bar -->
        <Loader
          v-if="issuesStore.loading"
          class="layout-regions__mobile-issues-loading hide-for-medium"
        />
        <LeafletMap
          v-if="loadMap && currentEquipment && !currentEquipment?.has_pending_tile_process"
          :key="issuesStore.componentKey"
          ref="map"
          :diagram="currentEquipment?.diagram_identifier"
          @init="onMapInit"
          @markerClick="selectIssueOnMap"
          @polygonClick="selectRegionOnMap"
        />
        <EmptyMessage
          v-else
          :message="$t('equipment.diagram_loading_description')"
          :title="$t('equipment.diagram_loading_title')"
        />
        <Loader
          v-if="!map && !currentEquipment?.has_pending_tile_process"
          class="show-for-medium"
          :modifiers="['overlay']"
        />
      </div>
    </div>

    <div
      v-if="mobileSearchOpen"
      class="layout-regions__mobile-search hide-for-medium"
    >
      <div class="layout-regions__search-bar">
        <UseSvg
          class="icon"
          name="search"
        />
        <input
          ref="mobileSearchInput"
          v-model="mobileSearch"
          class="layout-regions__search-input typo-small-title"
          :placeholder="$t('regions.search_regions_and_issues')"
        >
        <UseSvg
          class="icon"
          name="close_circle_24"
          @click="mobileSearchOpen = false"
        />
      </div>
      <EmptyMessage
        v-if="mobileSearch && regionResults.length === 0 && issueResults.length === 0"
        :message="$t('general.no_results')"
      >
        <UseSvg name="magnifying_glass_circle" />
      </EmptyMessage>
      <ul class="layout-regions__search-results scroll-container">
        <RegionItem
          v-for="region in regionResults"
          :key="region.id"
          :ignoreSelected="true"
          :project="false"
          :region="region"
          :showChildren="false"
          @select="selectRegionFromSearch"
        />
        <IssueCard
          v-for="issue in issueResults"
          :key="issue.id"
          isSmallCard
          :issue="issue"
          noLink
          :responsive="false"
          :showDescription="false"
          @select="selectIssueFromSearch(issue)"
        />
      </ul>
    </div>

    <Loader
      v-if="!map || !regionTree || !issueList"
      class="hide-for-medium"
      :modifiers="['overlay']"
    />
  </div>
  <router-view />
</template>

<script setup>
import { ref, computed, nextTick, reactive, inject, watch, onUnmounted, onMounted } from 'vue';
import I18n from 'i18n-js';
import booleanContains from '@turf/boolean-contains';
import { useRoute, useRouter } from 'vue-router';

import { DotIcon } from '@/vendor/leaflet_mods';

import ancestry from '@/functions/ancestry';
import regionPolygonOptions from '@/functions/region_polygon_options';
import { compareByAreaAndIdInverse } from '@/functions/compare_regions';
import { geoJSONPolygonToLatLngs, latLngToGeoJSON, rgeoPolygonToGeoJSON, rgeoToLatLng } from '@/functions/geospatial_conversions';
import getRiskRatingLevel from '@/functions/simm/get_risk_rating_level';
import Searcher from '@/functions/searcher';
import useAjax from '@/functions/use_ajax';
import { sortingFunctions, filterBy } from '@/functions/utils.js';
import { useActiveFilter } from '@/functions/composables/active_filter.js';
import debounce from '@/vendor/debounce';
import { useViewOptions } from '@/functions/options/view_option_items';
import { filterBySelectedRegion } from '@/functions/filter_by_selected_region.js';

// Stores
import { useIssuesStore } from '@/stores/issues/issuesStore';

// Components
import UseSvg from '@/components/generic/use_svg.vue';
import EmptyMessage from '@/components/generic/empty_message.vue';
import RecyclerView from '@/components/generic/recycler_view.vue';
import MobileHeader from '@/components/generic/mobile_header.vue';
import Loader from '@/components/generic/loader.vue';
import SwitchButton from '@/components/form/switch_button.vue';
import IssueCard from '@/components/issues/issue_card.vue';
import RegionItem from '@/components/regions/region_item.vue';
import RegionTree from '@/components/regions/region_tree.vue';
import LeafletMap from '@/vendor/components/leaflet_map.vue';
import IssueFilter from '@/components/issues/issue_filter.vue';

import IssuesSkeletonLoader from '@/components/issues/issues_skeleton_loader.vue';
import NoticeMessage from '@/components/generic/notice_message.vue';

// Icons
import PolygonSvg from '@/components/generic/polygon_svg.vue';
import PushDown from '@/images/push-down.svg';

// Set up store
const issuesStore = useIssuesStore();

const IssueIcon = DotIcon.extend({
  options: {
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  },
});

const RISK_ICONS = {
  // @ts-ignore
  low: new IssueIcon({ className: 'issue-point issue-point--low-risk' }),
  // @ts-ignore
  medium: new IssueIcon({ className: 'issue-point issue-point--medium-risk' }),
  // @ts-ignore
  significant: new IssueIcon({ className: 'issue-point issue-point--significant-risk' }),
  // @ts-ignore
  high: new IssueIcon({ className: 'issue-point issue-point--high-risk' }),
};

const SELECTED_RISK_ICONS = Object.entries(RISK_ICONS).reduce((obj, [risk, icon]) => {
  // @ts-ignore
  obj[risk] = new IssueIcon({ className: icon.options.className + ' issue-point--selected' });
  return obj;
}, {});

const props = defineProps({
  organisationId: { type: String, required: true },
  equipmentId: { type: String, required: true },
  issueId: { type: String, required: false, default: null },
  inspectionId: { type: String, required: false, default: null },
  issueType: { type: String, required: false, default: null },
});

const ajax = useAjax();
const sortFunctions = sortingFunctions();
const currentEquipment = inject('currentEquipment');
const route = useRoute();
const router = useRouter();

const regions = ref(null);
const issues = ref(null);
const showRegions = ref(false);
const sortBy = ref('');

const regionMap = ref(null);
const regionTree = ref([]);
const issueMap = ref(null);
const selectedRegion = ref(null);
const selectedIssue = ref(null);
const issueScrollView = ref(null);

const mobileSearchOpen = ref(false);
const mobileSearch = ref('');
const mobileSearchInput = ref(null);
const regionSearch = ref('');
const issueSearch = ref('');
const regionResults = ref([]);
const issueResults = ref([]);
const conditions = ref([]);
const priorities = ref([]);
const { isActiveFilter, validateFilterOptions } = useActiveFilter();
const { currentView, viewOptions } = useViewOptions();
const loadMap = ref(true);

let regionsSearcher = null;
let issuesSearcher = null;

async function reloadMap() {
  loadMap.value = false;
  await nextTick();
  loadMap.value = true;
}
/**
 * Reloads the map and update issues when filtering occurs
 */
watch(
  () => [sortBy.value, conditions.value, priorities.value],
  () => {
    reloadMap();
  }
);

const issueList = computed(() =>
  selectedRegion.value
    ? filterBySelectedRegion(filteredIssueList(), selectedRegion.value)
    : filteredIssueList()
);

function filteredIssueList(passedIssues) {
  if (!issueMap.value) return null;

  let list = passedIssues ? [...passedIssues] : [...issueMap.value.values()];

  if (conditions.value.length > 0) {
    list = list.filter(i => {
      return conditions.value.includes(i.overall_condition_rating);
    });
  }

  if (priorities.value.length > 0) {
    list = list.filter(i => {
      return priorities.value.includes(i.priority);
    });
  }

  if (sortBy.value === 'highest_repair_priority' || sortBy.value === 'lowest_repair_priority') {
    const highList = filterBy(list, 'priority', 'X');
    const lowList = filterBy(list, 'priority', 'M');
    const numberList = filterBy(list, 'priority')?.sort(sortFunctions[sortBy.value]);
    let finalList = [];

    if (sortBy.value === 'highest_repair_priority') {
      finalList = [...highList, ...numberList, ...lowList];
    } else if (sortBy.value === 'lowest_repair_priority') {
      finalList = [...lowList, ...numberList, ...highList];
    }
    return finalList;
  } else {
    return list.sort(sortFunctions[sortBy.value]);
  }
}

const sortByOptions = [
  { label: I18n.t('issues.highest_risk'), value: 'highest_risk'},
  { label: I18n.t('issues.lowest_risk'), value: 'lowest_risk'},
  { label: I18n.t('general.oldest'), value: 'oldest'},
  { label: I18n.t('general.newest'), value: 'newest'},
  { label: I18n.t('issues.highest_repair_priority'), value: 'highest_repair_priority'},
  { label: I18n.t('issues.lowest_repair_priority'), value: 'lowest_repair_priority'},
];

const map = ref(null);

onMounted(() => {
  loadIssues();
});

onUnmounted(() => {
  ajax.abort(); // Abort any ajax calls when leaving this component
});

watch(issuesStore, () => {
  renderIssues();
});

watch(mobileSearch, debounce(() => {
  reloadMap();
  if (!mobileSearchOpen.value || !mobileSearch.value) return;

  regionResults.value = regionsSearcher.search(mobileSearch.value);
  issueResults.value = issuesSearcher.search(mobileSearch.value);
}, 300, true));

watch(regionSearch, debounce(() => {
  if (!regionSearch.value) return;

  regionResults.value = regionsSearcher.search(regionSearch.value);
}, 300, true));

watch(issueSearch, debounce(() => {
  reloadMap();
  if (!issueSearch.value) return;

  issueResults.value = issuesSearcher.search(issueSearch.value);
}, 300, true));

async function loadRegions() {
  let { ok, data } = await ajax(`/organisations/${props.organisationId}/equipment/${props.equipmentId}/regions`);

  if (ok) {
    regions.value = data.regions;
    regionMap.value = new Map();
    if (!map.value) return; // in case user navigates away

    regions.value.forEach(loadRegion);
    regionTree.value = ancestry(regionMap.value.values());
  }
}

async function renderIssues() {
  issues.value = issuesStore.issues;
  issueMap.value = new Map();
  if (!map.value) return; // in case user navigates away

  const isActiveFilter = sortBy.value || conditions.value?.length > 0 || priorities.value?.length > 0;

  if (isActiveFilter) {
    const filteredIssues = filteredIssueList(issuesStore.issues);
    filteredIssues.forEach(loadIssue);
  } else {
    issues.value.forEach(loadIssue);
  }

  issuesSearcher = new Searcher(issueMap.value, { keys: ['title', 'id'] });

  refreshView();

  await loadSelectedContent();
}

async function loadIssues() {
  issuesStore.$reset();

  issues.value = [];
  issueMap.value = new Map();

  setTimeout(() => {
    issuesStore.loadingNotification = true;
  }, 5000);

  let { ok, data } = await ajax.paginated(
    `/organisations/${props.organisationId}/equipment/${props.equipmentId}/issues`,
    { query: { category: 'approved_and_open' }, items: issues.value }
  );

  if (ok) {
    issuesStore.issues = data;
  }

  issuesStore.setLoadingToFalse();
}

async function onMapInit() {
  await loadRegions();
  await renderIssues();
  await loadSelectedContent();
}

async function loadSelectedContent() {
  if (!map.value) return; // in case user navigates away

  if (route.query.regionId) {
    await selectRegion(regionMap.value.get(route.query.regionId), { redirect: false, goTo: !route.query.issueId });
  }
  if (route.query.issueId) {
    await selectIssue(issueMap.value.get(route.query.issueId), { redirect: false });
  }
}

function loadRegion(region) {
  let geoJSON = rgeoPolygonToGeoJSON(region.bounds);
  let latLngs = geoJSONPolygonToLatLngs(geoJSON);
  let polygon = map.value.addPolygon(latLngs, regionPolygonOptions(region.region_type));
  let regionObject = {
    ...region,
    geoJSON,
    latLngs,
    polygon,
  };
  polygon.disableEdit();
  polygon.region = regionObject;

  regionMap.value.set(region.id, reactive(regionObject));

  regionsSearcher = new Searcher(regionMap.value);
}

function loadIssue(issue) {
  let latLng = rgeoToLatLng(issue.location.location);
  let geoJSON = latLngToGeoJSON(latLng);
  let riskLevel = getRiskRatingLevel(issue.risk_rating);
  let zIndexOffset = issue.risk_rating * 10;
  let marker = map.value.addMarker(latLng, {
    icon: RISK_ICONS[riskLevel],
    riseOnHover: true,
    zIndexOffset,
  });
  let issueObject = {
    ...issue,
    timestamp: +new Date(issue.inspection.date),
    riskLevel,
    latLng,
    geoJSON,
    marker,
    zIndexOffset,
  };
  marker.issue = issueObject;

  issueMap.value.set(issue.id, issueObject);

  if (issue.id === route.query.issueId) map.value?.enableCrosshair(marker._latlng.lat, marker._latlng.lng);
}

async function refreshView() {
  await nextTick();

  switch (currentView.value) {
    case 'main':
      if (map.value) await map.value.refresh();
      break;
    case 'list':
      issueScrollView.value.update();
      scrollToIssue(selectedIssue.value, { behavior: 'auto', block: 'top', force: true });
      break;
  }
}

// make sure clicking on a stack of objects doesn't trigger a click on all of them
let selectingObject = false;
function doSelect() {
  selectingObject = true;
  setTimeout(() => selectingObject = false, 0);
}

function deselectRegion() {
  if (selectedRegion.value) {
    let previousRegion = regionMap.value.get(selectedRegion.value.id);
    previousRegion.polygon.setStyle(regionPolygonOptions(previousRegion.region_type, false));
    previousRegion.selected = false;
  }
}

async function selectRegion(region, { goTo = true, redirect = true } = {}) {
  if (!region) return selectNoRegion({ goTo });
  if (selectingObject && goTo) return;
  selectingObject = true;

  showRegions.value = false;
  deselectRegion();

  let newRegion = regionMap.value.get(region.id);
  newRegion.polygon.setStyle(regionPolygonOptions(newRegion.region_type, true));
  selectedRegion.value = newRegion;

  if (goTo) map.value.goToObject(newRegion.polygon);

  if (selectedIssue.value) {
    if (!booleanContains(region.geoJSON, selectedIssue.value.geoJSON)) {
      deselectIssue();
    } else {
      await nextTick();
      scrollToIssue(selectedIssue.value, { behavior: 'smooth', block: 'top', force: true });
    }
  }

  if (redirect) {
    await router.replace({ query: { regionId: region.id, issueId: selectedIssue.value?.id } });
  }

  await nextTick();

  await new Promise(resolve => {
    newRegion.selected = true;
    setTimeout(() => {
      selectingObject = false;
      resolve();
    }, 0);
  });
}

function selectRegionOnMap(polygon) {
  if (polygon.region) {
    selectRegion(polygon.region);
  }
}

function selectNoRegion({ goTo = true } = {}) {
  if (selectingObject && goTo) return;
  doSelect();

  router.replace({ query: { regionId: null } });

  showRegions.value = false;
  deselectRegion();

  selectedRegion.value = null;
}

async function selectIssueOnMap(marker) {
  doSelect();

  currentView.value = 'main';
  await selectIssue(marker.issue);
}

async function selectIssueOnList(issue) {
  currentView.value = 'main';
  await refreshView();
  await selectIssue(issue);
}

async function selectIssue(issue, { redirect = true } = {}) {
  deselectIssue();
  let newIssue = issueMap.value.get(issue?.id);
  if (!newIssue) return;
  newIssue.marker.setIcon(SELECTED_RISK_ICONS[newIssue.riskLevel]);
  newIssue.marker.setZIndexOffset(newIssue.zIndexOffset + 1000);
  map.value.showMarker(newIssue.marker);
  selectedIssue.value = newIssue;

  if (selectedRegion.value && !booleanContains(selectedRegion.value.geoJSON, issue.geoJSON)) {
    await selectSmallestEnclosingRegion(issue);
  }

  if (redirect) {
    router.replace({ query: { regionId: selectedRegion.value?.id, issueId: issue.id } });
  }

  if (issue?.latLng[0] && issue?.latLng[1]) map.value?.enableCrosshair(issue.latLng[0], issue.latLng[1]);

  await nextTick();
  map.value?.goToObject(newIssue.marker);
  scrollToIssue(newIssue, { behavior: 'smooth', block: 'top', force: true });
}

async function selectSmallestEnclosingRegion(issue) {
  let containingRegions = [...regionMap.value.values()]
    .filter(region => booleanContains(region.geoJSON, issue.geoJSON))
    .sort(compareByAreaAndIdInverse);
  if (containingRegions.length > 0) {
    await selectRegion(containingRegions[0], { goTo: false });
  } else {
    selectNoRegion({ goTo: false });
  }
}

function deselectIssue() {
  if (selectedIssue.value) {
    let previousIssue = issueMap.value.get(selectedIssue.value.id);
    if (!previousIssue) return;
    previousIssue.marker.setIcon(RISK_ICONS[previousIssue.riskLevel]);
    previousIssue.marker.setZIndexOffset(previousIssue.zIndexOffset);
    selectedIssue.value = null;
  }
}

function scrollToIssue(issue, options = {}) {
  const scrollToIndex = issue ? issueList.value.indexOf(issue) : 0;
  const issueCardElement = issueScrollView.value.$el.querySelector('.issue-card');
  const elementHeight = issueCardElement?.style?.height?.replace('rem', '');
  const elementOffset = ((parseFloat(elementHeight) * 16) + 2) * scrollToIndex; // 1rem = 16px
  options.top = elementOffset;
  setTimeout(() => {
    issueScrollView.value?.$el?.scrollTo(options);
  }, 300);
}

const csvIssueDownloadPath = computed(() => {
  let endpoint = `/internal/organisations/${props.organisationId}/equipment/${props.equipmentId}/graph/download?type=multiple`;

  endpoint += conditions.value.map((item) => `&conditions[]=${item}`).join('');
  endpoint += priorities.value.map((item) => `&priorities[]=${item}`).join('');

  // need to append the selected region id to the end point url
  if (route.query.regionId) {
    endpoint += `&region_id=${route.query.regionId}&issue_type=region`;
  }

  if (issues.value?.length > 0) {
    const earliestIssue = issues.value.reduce((start, current) => {
      return start['inspection']['date'] < current['inspection']['date'] ? start : current;
    });

    if (earliestIssue) {
      endpoint += `&start_date=${earliestIssue['inspection']['date']}`;
    }
  }

  return endpoint;
});

function setFilterOptions(options) {
  sortBy.value = options.sortOption;
  conditions.value = options.conditions;
  priorities.value = options.priorities;

  /* Check if filter has any active filter options */
  validateFilterOptions(options);
}

async function openMobileSearch() {
  mobileSearchOpen.value = true;
  mobileSearch.value = '';
  regionResults.value = [];
  issueResults.value = [];

  await nextTick();
  mobileSearchInput.value.focus();
}

async function selectIssueFromSearch(issue) {
  mobileSearchOpen.value = false;
  issueSearch.value = '';
  await selectIssue(issue);
}

async function selectRegionFromSearch(region) {
  mobileSearchOpen.value = false;
  regionSearch.value = '';
  await selectRegion(region);
}
</script>
